import { brand } from "@/constants/colors";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BottomSheetBackdrop, BottomSheetModal } from "@gorhom/bottom-sheet";
import type React from "react";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

export interface AppBottomSheetModalProps {
  snapPoints?: (string | number)[];
  children?: React.ReactNode;
  handleIndicatorStyle?: object;
  enablePanDownToClose?: boolean;
  enableDynamicSizing?: boolean;
  onDismiss?: () => void;
}

export interface AppBottomSheetModalRef {
  present: () => void;
  dismiss: () => void;
}

const AppBottomSheetModal = forwardRef<
  AppBottomSheetModalRef,
  AppBottomSheetModalProps
>(
  (
    {
      snapPoints = ["50%"],
      children,
      handleIndicatorStyle = {},
      enablePanDownToClose = true,
      enableDynamicSizing = false,
      onDismiss,
    },
    ref
  ) => {
    const bottomSheetModalRef = useRef<BottomSheetModal>(null);
    const backgroundColor = useThemeColor({}, "background");
    const finalSnapPoints = useMemo(() => snapPoints, [snapPoints]);

    const renderBackdrop = useCallback(
      (props: any) => (
        <BottomSheetBackdrop
          {...props}
          appearsOnIndex={0}
          disappearsOnIndex={-1}
          pressBehavior="close"
        />
      ),
      []
    );

    useImperativeHandle(ref, () => ({
      present: () => bottomSheetModalRef.current?.present(),
      dismiss: () => bottomSheetModalRef.current?.dismiss(),
    }));

    return (
      <BottomSheetModal
        ref={bottomSheetModalRef}
        index={0}
        snapPoints={finalSnapPoints}
        backdropComponent={renderBackdrop}
        enablePanDownToClose={enablePanDownToClose}
        enableDynamicSizing={enableDynamicSizing}
        backgroundStyle={{
          backgroundColor,
        }}
        handleIndicatorStyle={{
          backgroundColor: brand.primary.main,
          ...handleIndicatorStyle,
        }}
        onDismiss={onDismiss}
      >
        {children}
      </BottomSheetModal>
    );
  }
);

AppBottomSheetModal.displayName = "AppBottomSheetModal";

export default AppBottomSheetModal;
